import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { whiteLabelService } from '../../services/whiteLabelService';
import { formsService } from '../../services/formsService';
import { useAuth } from '../../contexts/AuthContext';
import PortalLoginPage from './components/PortalLoginPage';
import PortalLayout from './components/PortalLayout';

// ─── Helpers ───────────────────────────────────────────────────────────────────
const defaultBranding = {
  agencyName: 'Your Agency',
  logoUrl: '',
  faviconUrl: '',
  primaryColor: '#107570',
  emailFooterText: '',
  customDomain: '',
  isEnabled: false,
};

const defaultEmbedStyle = {
  bgColor: '#ffffff',
  borderColor: '#e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  fontFamily: 'DM Sans',
  fontSize: '14px',
};

const defaultCustomerConfig = {
  portalTitle: '',
  supportEmail: '',
  privacyUrl: '/privacy',
  termsUrl: '/terms',
  successMessage: '',
  showPoweredBy: true,
  showFormTitle: true,
};

// ─── FormCard ──────────────────────────────────────────────────────────────────
const FormCard = ({ form, branding, embedStyle, customerConfig, onOpen }) => {
  const primaryColor = branding?.primaryColor || '#107570';
  const radius = embedStyle?.borderRadius || '8px';

  return (
    <div
      className="group cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
      style={{
        backgroundColor: embedStyle?.bgColor || '#ffffff',
        border: `1px solid ${embedStyle?.borderColor || '#e2e8f0'}`,
        borderRadius: radius,
        boxShadow: embedStyle?.boxShadow !== 'none' ? embedStyle?.boxShadow : undefined,
        fontFamily: embedStyle?.fontFamily !== 'System Default' ? embedStyle?.fontFamily : undefined,
        fontSize: embedStyle?.fontSize || '14px',
        overflow: 'hidden',
      }}
      onClick={() => onOpen(form)}
    >
      <div className="h-1 w-full" style={{ backgroundColor: primaryColor }} />
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primaryColor}18` }}
          >
            <Icon name="FileText" size={18} color={primaryColor} />
          </div>
          <div className="flex-1 min-w-0">
            {customerConfig?.showFormTitle !== false && (
              <h3 className="font-semibold text-gray-900 truncate leading-tight" style={{ fontSize: embedStyle?.fontSize || '14px' }}>
                {form?.name || 'Untitled Form'}
              </h3>
            )}
            {form?.description && (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{form?.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
          <span className="flex items-center gap-1">
            <Icon name="Layers" size={11} />
            {form?.fields?.length || 0} fields
          </span>
          {form?.is_multi_step && (
            <span className="flex items-center gap-1">
              <Icon name="GitBranch" size={11} />
              Multi-step
            </span>
          )}
          <span
            className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-medium"
            style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
          >
            Active
          </span>
        </div>
        <button
          type="button"
          className="w-full py-2 text-sm font-medium text-white rounded-md transition-opacity hover:opacity-90 focus:outline-none"
          style={{ backgroundColor: primaryColor, borderRadius: radius }}
        >
          Open Form
        </button>
      </div>
    </div>
  );
};

// ─── FormModal ─────────────────────────────────────────────────────────────────
const FormModal = ({ form, branding, embedStyle, customerConfig, onClose }) => {
  const primaryColor = branding?.primaryColor || '#107570';
  const radius = embedStyle?.borderRadius || '8px';
  const domain = branding?.customDomain || window.location?.host;

  if (!form) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: embedStyle?.bgColor || '#ffffff',
          borderRadius: radius,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          fontFamily: embedStyle?.fontFamily !== 'System Default' ? embedStyle?.fontFamily : undefined,
          fontSize: embedStyle?.fontSize || '14px',
        }}
      >
        <div className="h-1 w-full rounded-t-[inherit]" style={{ backgroundColor: primaryColor }} />
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: embedStyle?.borderColor || '#e2e8f0' }}>
          <div>
            {customerConfig?.showFormTitle !== false && (
              <h2 className="font-semibold text-gray-900" style={{ fontSize: '16px' }}>
                {form?.name || 'Untitled Form'}
              </h2>
            )}
            {form?.description && <p className="text-xs text-gray-500 mt-0.5">{form?.description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
        <div className="px-6 py-5">
          <div
            className="rounded-md border p-4 text-center"
            style={{ borderColor: embedStyle?.borderColor || '#e2e8f0', borderRadius: radius }}
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${primaryColor}15` }}>
              <Icon name="ExternalLink" size={20} color={primaryColor} />
            </div>
            <p className="text-sm text-gray-600 mb-4">This form is hosted at:</p>
            <code className="block text-xs px-3 py-2 rounded-md mb-4 break-all" style={{ backgroundColor: `${primaryColor}08`, color: primaryColor }}>
              https://{domain}/f/{form?.id}
            </code>
            <a
              href={`/${form?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-md transition-opacity hover:opacity-90"
              style={{ backgroundColor: primaryColor, borderRadius: radius }}
            >
              <Icon name="ExternalLink" size={14} />
              Open Full Form
            </a>
          </div>
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-500 mb-2">Embed this form:</p>
            <div className="rounded-md p-3 overflow-x-auto" style={{ backgroundColor: '#f8fafc', border: `1px solid ${embedStyle?.borderColor || '#e2e8f0'}` }}>
              <pre className="text-xs text-gray-600 whitespace-pre-wrap break-all font-mono">{`<iframe src="https://${domain}/f/${form?.id}" width="100%" height="600" frameborder="0" style="border:none;border-radius:${radius}"></iframe>`}</pre>
            </div>
          </div>
        </div>
        {customerConfig?.showPoweredBy !== false && (
          <div className="px-6 py-3 border-t text-center" style={{ borderColor: embedStyle?.borderColor || '#e2e8f0' }}>
            <p className="text-xs text-gray-400">Powered by {branding?.agencyName || 'Your Agency'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Empty State ───────────────────────────────────────────────────────────────
const EmptyState = ({ primaryColor }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${primaryColor}15` }}>
      <Icon name="FileText" size={28} color={primaryColor} />
    </div>
    <h3 className="text-lg font-semibold text-gray-800 mb-2">No forms available</h3>
    <p className="text-sm text-gray-500 max-w-xs">No published forms are available in this portal yet. Check back soon.</p>
  </div>
);

// ─── Main Page ─────────────────────────────────────────────────────────────────
const WhiteLabelCustomerPortal = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [branding, setBranding] = useState(defaultBranding);
  const [embedStyle] = useState(defaultEmbedStyle);
  const [customerConfig, setCustomerConfig] = useState(defaultCustomerConfig);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  const primaryColor = branding?.primaryColor || '#107570';

  useEffect(() => {
    const load = async () => {
      try {
        const [wlSettings, formsData] = await Promise.all([
          whiteLabelService?.getSettings()?.catch(() => null),
          formsService?.getForms()?.catch(() => []),
        ]);

        if (wlSettings?.customerConfig && Object.keys(wlSettings.customerConfig)?.length > 0) {
          setCustomerConfig((prev) => ({ ...prev, ...wlSettings.customerConfig }));
        }
        if (wlSettings) {
          setBranding({
            agencyName: wlSettings?.agencyName || 'Your Agency',
            logoUrl: wlSettings?.logoUrl || '',
            faviconUrl: wlSettings?.faviconUrl || '',
            primaryColor: wlSettings?.primaryColor || '#107570',
            emailFooterText: wlSettings?.emailFooterText || '',
            customDomain: wlSettings?.customDomain || '',
            isEnabled: wlSettings?.isEnabled || false,
          });
        }

        const publishedForms = (formsData || [])?.filter(
          (f) => f?.status === 'published' || f?.is_published
        );
        setForms(publishedForms);
      } catch (err) {
        console.error('Portal load error:', err);
        setError('Unable to load portal. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      load();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const filteredForms = forms?.filter((f) =>
    !searchQuery ||
    f?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    f?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const portalTitle = customerConfig?.portalTitle || branding?.agencyName
    ? `${customerConfig?.portalTitle || branding?.agencyName} — Forms Portal`
    : 'Client Portal';

  // Auth loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${primaryColor}40`, borderTopColor: primaryColor }} />
          <p className="text-sm text-gray-500">Loading…</p>
        </div>
      </div>
    );
  }

  // Not authenticated — show branded login
  if (!user) {
    return (
      <>
        <Helmet>
          <title>{portalTitle}</title>
          {branding?.faviconUrl && <link rel="icon" href={branding?.faviconUrl} />}
        </Helmet>
        <PortalLoginPage
          branding={branding}
          embedStyle={embedStyle}
          customerConfig={customerConfig}
          onLoginSuccess={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{portalTitle}</title>
        {branding?.faviconUrl && <link rel="icon" href={branding?.faviconUrl} />}
        <meta name="description" content={`Access forms from ${branding?.agencyName || 'our portal'}`} />
      </Helmet>

      <PortalLayout branding={branding} customerConfig={customerConfig} pageTitle="Forms">
        {/* ── Page Header ── */}
        <div
          className="border-b px-4 sm:px-6 lg:px-8 py-8"
          style={{
            background: `linear-gradient(135deg, ${primaryColor}08 0%, ${primaryColor}04 100%)`,
            borderColor: `${primaryColor}20`,
          }}
        >
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 leading-tight">
                  {customerConfig?.portalTitle || `${branding?.agencyName || 'Your'} Forms`}
                </h1>
                <p className="text-sm text-gray-500">Browse and access all available forms below.</p>
              </div>
              {/* Search */}
              <div className="relative w-full sm:w-72">
                <Icon name="Search" size={16} color="#9ca3af" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search forms…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none transition-all"
                  style={{ borderColor: embedStyle?.borderColor || '#e2e8f0', borderRadius: embedStyle?.borderRadius || '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
                  onFocus={(e) => { e.target.style.borderColor = primaryColor; e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`; }}
                  onBlur={(e) => { e.target.style.borderColor = embedStyle?.borderColor || '#e2e8f0'; e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
                />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <Icon name="X" size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Forms Grid ── */}
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${primaryColor}40`, borderTopColor: primaryColor }} />
                <p className="text-sm text-gray-500">Loading forms…</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <Icon name="AlertCircle" size={22} color="#ef4444" />
              </div>
              <p className="text-gray-700 font-medium mb-1">Something went wrong</p>
              <p className="text-sm text-gray-500">{error}</p>
            </div>
          ) : (
            <>
              {filteredForms?.length > 0 && (
                <div className="flex items-center justify-between mb-5">
                  <p className="text-xs text-gray-400">
                    {filteredForms?.length} form{filteredForms?.length !== 1 ? 's' : ''} available
                    {searchQuery && ` for "${searchQuery}"`}
                  </p>
                </div>
              )}
              {filteredForms?.length === 0 ? (
                <EmptyState primaryColor={primaryColor} />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredForms?.map((form) => (
                    <FormCard
                      key={form?.id}
                      form={form}
                      branding={branding}
                      embedStyle={embedStyle}
                      customerConfig={customerConfig}
                      onOpen={setSelectedForm}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </PortalLayout>

      {/* ── Form Modal ── */}
      {selectedForm && (
        <FormModal
          form={selectedForm}
          branding={branding}
          embedStyle={embedStyle}
          customerConfig={customerConfig}
          onClose={() => setSelectedForm(null)}
        />
      )}
    </>
  );
};

export default WhiteLabelCustomerPortal;
